import { action, computed, observable, reaction } from "mobx";
import { cloneDeep, debounce, isEqual, omit, sortBy } from "lodash";
import { FormErrors, FormStore } from "@modules/formx";
import {
  BookingEdit_FetchDepts,
  ConsultantContract_AddUnSign,
  ConsultantContract_Edit,
  ConsultantContract_getByIdForConfirmation,
  ConsultantContract_getByIdForDuplication,
  ConsultantContract_getByIdForEdit,
  ConsultantContract_GetConsultant,
  ConsultantContract_getConsultantPayoutByYear,
  ConsultantContract_GetConsultantQuery,
  ConsultantContract_getContractsByConsultantId,
  ConsultantContract_GetCustomer,
  ConsultantContract_getCustomerPriceList,
  ConsultantContract_getHighHolidays,
  ConsultantContract_GetParentOrganizationType,
  ConsultantContractCustomerDataFragment,
  ConsultantContractDetailsFragment,
  ConsultantContractOvertimeOpt,
  ConsultantContractTableRowDataFragment,
  ConsultantContractTravelsPeriod,
  ConsultantContractType,
  ConsultantEventLog_GetEvents,
  ConsultantPayoutByYear,
  ContractDataForDuplicationFragment,
  ContractsPriceTemplateFragment,
  ContractsPriceTemplates,
  Customer,
  CustomerType,
  GetConsultantContracts,
  HighHolidayOptionFragment,
  LegalPersonType,
  OrganizationType,
  PublishConsultantContract,
  SendEmail,
  PublishConsultantContractViaWorkflow,
  UploadConsultantContractDocument,
  ValueLabel,
  ContractType
} from "@graphql";
import { getObservableQuery } from "@graphql/getObservableQuery";
import { store } from "@store";
import locale, { dateFormat } from "@bonliva-crm/locale";
import { CONSULTANT_CONTRACT_SLIDER, CONSULTANT_SLIDER } from "@navigation";
import {
  timeReportService,
  ValidateConsultantContractOptionResult
} from "@api/timereports";
import {
  BC_contractEmployeesTypeOptions,
  BC_contractsTypes,
  BC_contractSubTypeOptions,
  CompanyName,
  contractEmployeesTypeOptions,
  contractSubTypeOptions,
  defaultProof,
  formatContractTypeByNational,
  FormData,
  getConfirmationContractType,
  getContractWarning,
  handleSetPriceTemplateOnOvertimeChange,
  initAddress,
  initialValues as defaultValues,
  listOfContractStrategyHourly,
  National_contractsTypes,
  notional_contractEmployeesTypeOptions,
  notional_contractSubTypeOptions,
  oneSpecificPeriodPrices,
  RadioTrueFalse,
  splitStrategies
} from "./utils";
import {
  bc_professions,
  contracts,
  defaultFields,
  intermittentBase,
  national_professions
} from "./formSchemas";
import {
  FieldProps,
  getSchema
} from "@consultants/ConsultantContract/DynamicFieldsGroup";
import moment from "moment";
import { createObjectID } from "@bonliva-crm/common";
import { ContractPriceTemplateRecordInput } from "@config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { mergeHighHolidays } from "@config_editor/ContractsPriceTemplates/utils";
import { openBlank } from "@clientUtils";
import { OkCancelDialogOptions } from "@containers";
import { editsProof, payoutTypeEmp, payoutTypeSub } from "./fields";
import { checkArchivedPriceList, PriceOption, sortPriceOptions } from "../utils";
import WeeklyHoursWorkedBonus from "@consultants/components/Bonus/WeeklyHoursWorkedBonus";
import WorkedHourBonus from "@consultants/components/Bonus/WorkedHourBonus";
import WorkedHourPeriodBonus from "@consultants/components/Bonus/WorkedHourBonus";
import OvertimeBonus from "@consultants/components/Bonus/OvertimeBonus";
import CustomBonus from "@consultants/components/Bonus/CustomBonus";
import {
  ConsultantContractPensionSolution,
  ConsultantContractStrategyFactory,
  Professions,
  replaceEmailDomainByTenant,
  Tenant
} from "@bonliva-crm/shared";
import { getConsultantAgeGroup } from "@bonliva-crm/shared";
import yup from "yup";
import { OkDialogOptions } from "@containers/ConfirmDialog/OkDialogOptions";

export { FormData };

type CustomerContracts =
  ConsultantContract_getCustomerPriceList.Return["getCustomerContractPriceLists"];
type CustomerPriceListItem = CustomerContracts[0]["priceLists"][0];

type SelectedCustomerTemplate = {
  records: any[];
  highHolidays: any[];
};

export class ContractFormStore {
  form: FormStore<FormData>;
  handleClose: () => void;

  @observable tenant: Tenant = null;
  @observable isLoadingDepartments = false;
  @observable loading = false;
  @observable isEdit = false;
  @observable action: string = null;
  @observable isTicket = false;
  @observable isPublishingToOneFlow = false;
  @observable ticketData = null;
  @observable defaultTemplateWarning: string = null;
  @observable disabledSalaryRecord = false;
  @observable _fields: FieldProps[] = defaultFields;
  @observable allDepartments: LabelValue[] = null;
  @observable selectedCustomer: ConsultantContractCustomerDataFragment | null = null;
  @observable contractData: ConsultantContractDetailsFragment | null = null;
  @observable currentConsultant:
    | ConsultantContract_GetConsultantQuery["consultantById"]
    | null = null;
  @observable lastContract: ConsultantContract_GetConsultantQuery["lastContract"] | null =
    null;
  @observable isSalaryChoiceDialogRequired = true;
  @observable isSalaryChoiceDialogOpen = false;
  @observable preFilledData: FormData = null;
  @observable fileName: string = null;
  @observable inNorwayTeam: boolean;
  @observable isParentKommun: boolean = false;
  @observable templates: ContractsPriceTemplateFragment[] = [];
  @observable highHolidaysOptions: HighHolidayOptionFragment[];
  @observable payouts: ConsultantPayoutByYear[];
  @observable fav: ContractsPriceTemplateFragment[] = [];
  @observable
  customerPriceTemplates: ContractsPriceTemplateFragment[] = [];
  @observable customerPriceOptions: PriceOption[];
  @observable company: CompanyName | null = null;
  @observable isNationalProcurement: RadioTrueFalse | null = null;
  @observable showContractTypeChangeSelect = false;
  @observable maxEndDate: Date | null = null;
  @observable
  customerContracts: CustomerContracts;
  consultantBookingContracts: ConsultantContract_getCustomerPriceList.Return["consultantBookingContracts"];
  selectedCustomerTemplate: SelectedCustomerTemplate;
  originalCustomer: Customer;
  @observable minReq: string[] = [
    "ContractType",
    "HospitalRef",
    "DepartmentsRefs",
    "AreaOfExpertise",
    "Profession"
  ];

  bonusTypes: string[] = [
    "WeeklyHoursWorkedBonus",
    "WorkedHourBonus",
    "OvertimeBonus",
    "CustomBonus"
  ];

  @observable validationResult: ValidateConsultantContractOptionResult[] = [];

  @computed get contractStrategy() {
    if (!this.currentConsultant || !this.form.values.ContractType) {
      return null;
    }

    return ConsultantContractStrategyFactory.create({
      type: this.form.values.ContractType,
      periodStart: moment(this.form.values.StartDate).format(dateFormat),
      periodEnd: moment(this.form.values.EndDate).format(dateFormat),
      profession: this.form.values.Profession,
      pensionSolution: this.form.values.pensionSolution,
      consultantBirthDate: moment(this.currentConsultant.BirthDate).format(dateFormat)
    });
  }

  @computed get isSalaryChoiceRequired(): boolean {
    return (
      this.tenant === "wcc" &&
      this.contractStrategy &&
      splitStrategies.includes(this.contractStrategy?.name)
    );
  }

  // For Profession “Sjuksköterska” och “Barnmorska” for EmploymentNationalFullContractStrategy and IntermittentNationalFullContractStrategy
  @computed get hideOvertimeForNPFullStrategy(): boolean {
    return (
      (this.form?.values?.Profession === Professions.SE_SSK ||
        this.form?.values?.Profession === Professions.SE_BARNMORSKA) &&
      (this.contractStrategy?.name === "EmploymentNationalFullContractStrategy" ||
        this.contractStrategy?.name === "IntermittentNationalFullContractStrategy")
    );
  }

  @computed get isNationalProcurementPriceStrategy(): boolean {
    if (this.currentConsultant?.LegalPersonType === LegalPersonType.Subcontractor) {
      return false;
    }

    const contractStrategy = this.contractStrategy;

    if (!contractStrategy) {
      return false;
    }

    if (contractStrategy.name === "ConfirmationForEmployeesNationalContractStrategy") {
      return this.form.values.isNationalProcurementPriceStrategy;
    }

    return (
      contractStrategy.name === "EmploymentNationalFullContractStrategy" ||
      contractStrategy.name === "IntermittentNationalFullContractStrategy"
    );
  }

  @computed get basicPriceLabel() {
    if (this.isNationalProcurementPriceStrategy) {
      return locale.BasicPriceNationalProcurementPriceStrategy;
    }

    if (listOfContractStrategyHourly.includes(this.contractStrategy?.name)) {
      return locale.SalaryCompensationHourly;
    } else {
      return locale.SalaryCompensation;
    }
  }

  @computed get fields() {
    return (
      this._fields?.map((x) => {
        if (x.name === "SalaryRecords") {
          return {
            ...x,
            schema: {
              ...x.schema,
              BasicPrice: yup
                .number()
                .nullable(true)
                .required(locale.fieldIsMandatory(this.basicPriceLabel))
            }
          };
        } else {
          return x;
        }
      }) ?? []
    );
  }

  @computed get noFields(): boolean {
    return this.fields?.length === 0;
  }

  constructor(
    initialValues: FormData,
    consultantId: string,
    handleClose,
    tenant: Tenant,
    contractId?: string,
    action?: string
  ) {
    this.form = new FormStore<FormData>({
      onSubmit: this.onSubmit,
      initialValues,
      onSetValue: this._onSetValue,
      validate: (formStore) => {
        const errors: FormErrors<FormData> = {};
        if (
          this.isNationalContractTypeSelected &&
          this.currentConsultant?.LegalPersonType === LegalPersonType.Employee &&
          (this.consultantPension?.becomes25 || this.consultantPension?.becomes65) &&
          !formStore.values.pensionSolution
        ) {
          errors.pensionSolution = locale.consultantContractPensionSolution.getLabel(
            this.consultantPension.becomes25 ? 25 : 65
          );
        }

        if (this.allDepartments?.length > 0 && !formStore.values.DepartmentsRefs) {
          errors.DepartmentsRefs = locale.fieldIsMandatory(locale.Department);
        }

        if (this.isSalaryChoiceRequired) {
          if (
            !formStore.values.SalaryChoice ||
            formStore.values.SalaryChoice === ContractType.NationalProcurement
          ) {
            errors.SalaryChoice = locale.fieldIsMandatory(locale.SalaryChoice);
          }
          if (
            formStore.values.SalaryChoice === ContractType.HourlyPensionInsurance &&
            this.consultantPension.above60
          ) {
            errors.SalaryChoice = "Konsulten kan inte längre välja val 3";
          }
          if (
            this.isSalaryChoiceDialogRequired &&
            !this.isSalaryChoiceDialogOpen &&
            this.form.values.StartDate &&
            this.form.values.EndDate
          ) {
            this.handleSalaryChoiceWarning();
          }
        }

        return errors;
      }
    });

    this.handleClose = handleClose;
    this.getCurrentConsultant(consultantId);

    this.isEdit = action === "edit";
    this.action = action;
    this.tenant = tenant;
    this.isNationalProcurement = tenant === "wcc" ? "true" : null;
    this.inNorwayTeam = store().user.inTeam("crm-team-norge");
    // set default country in which the consultant will work
    this.form.values.Country = this.inNorwayTeam ? "Norway" : "Sweden";

    this.getHolidayOptions();
    this.getConsultantPayoutByYear(consultantId);

    if (contractId) {
      this.getContractData(contractId, action).then((x) => {
        if (this.contractData?.SignedOn) {
          const options = new OkCancelDialogOptions("Edit signed contract?");
          options.message =
            "This contract is already signed. Are you sure you want to edit it? This can cause discrepancy with the signed version";
          store()
            .ui.confirmDialog.confirm(options)
            .then((res) => {
              if (res === "cancel") {
                this.form.reset(this.form.values, true);
                setTimeout(() => {
                  if (store().navigation.hasInstance(CONSULTANT_CONTRACT_SLIDER)) {
                    store().navigation.close(CONSULTANT_CONTRACT_SLIDER);
                  } else {
                    store().navigation.close(CONSULTANT_SLIDER);
                  }
                }, 50);
              } else {
                this.addEditsProofFields();
              }
            });
        }
      });
    } else {
      this.getPriceTemplates();
    }

    reaction(
      () => this.preFilledData,
      (e) => {
        if (e) {
          this.form.reset(e);

          this.company = BC_contractsTypes.includes(e.ContractType) ? "BC" : "AB";
          this.isNationalProcurement = National_contractsTypes.includes(e.ContractType)
            ? "true"
            : "false";

          if (e.HospitalRef) {
            this.fetchHospitalData(e.HospitalRef.value);
          }
          if (e.Overtime === ConsultantContractOvertimeOpt.SKLBasedOvertime) {
            handleSetPriceTemplateOnOvertimeChange(
              this.form,
              this.templates,
              this.handleChangeTemplate,
              this.setNoTemplateWarning,
              this.setDisabledSalaryRecord,
              true
            );
          }

          // Get customer ID from either the first department or hospital reference
          const departmentId = e.DepartmentsRefs?.[0];
          const hospitalId = e.HospitalRef?.value;
          const customerId = departmentId || hospitalId;

          if (customerId) {
            this.selectCustomer(customerId);
          }

          this._fields = this.getFields({
            parentContractId: e.ParentContractId,
            contractType: this.form.values.ContractType,
            profession: this.form.values.Profession
          });

          if (customerId) {
            this.getCustomerContractPriceLists([customerId]);
          }
        }
      }
    );
    (window as any).contractFormStore = this;
  }

  getFields = ({
    parentContractId,
    contractType,
    profession
  }: {
    contractType: ConsultantContractType;
    parentContractId?: string;
    profession?: string;
  }): FieldProps[] => {
    if (parentContractId) {
      return profession
        ? contracts[contractType][profession]
        : contracts[contractType][Professions.SE_LAKARE];
    }

    return profession
      ? contracts[this.form.values.ContractType][profession]
      : intermittentBase;
  };

  _onSetValue = ({ name, value }) => {
    // change validation form when set ContractType to Ramavtal
    if (name === "ContractType") {
      if (this.form.values.ContractType === ConsultantContractType.Intermittent) {
        if (this.form.values.Profession) {
          this._fields = contracts[value][this.form.values.Profession];
        } else {
          this._fields = intermittentBase;
        }
        return;
      }
      if (
        value === ConsultantContractType.BC_Framework ||
        value === ConsultantContractType.BC_Intermittent
      ) {
        this.form.setValue("SalaryRecords", []);
      }
      if (value) {
        this._fields = this.form.values.Profession
          ? contracts[value][this.form.values.Profession]
          : defaultFields;
      } else {
        this._fields = defaultFields;
      }
      if (
        value === ConsultantContractType.Intermittent_National ||
        value === ConsultantContractType.Employment_National
      ) {
        this.minReq = [...this.minReq, "StartDate", "EndDate"];
      } else {
        this.minReq = this.minReq.filter((x) => x !== "StartDate" && x !== "EndDate");
      }
    }

    if (name === "Profession") {
      this._fields = this.form.values.ContractType
        ? contracts[this.form.values.ContractType][value]
        : defaultFields;
    }

    if (name === "Overtime") {
      handleSetPriceTemplateOnOvertimeChange(
        this.form,
        this.templates,
        this.handleChangeTemplate,
        this.setNoTemplateWarning,
        this.setDisabledSalaryRecord
      );
    }

    if (name === "AreaOfExpertise" || name === "StartDate" || name === "EndDate") {
      this.formatCustomerPriceTemplatesOptions(
        this.customerContracts,
        this.consultantBookingContracts
      );
    }

    if (name === "SalaryChoice" || name === "StartDate" || name === "EndDate") {
      if (this.isSalaryChoiceRequired) {
        this.isSalaryChoiceDialogRequired = true;
        this.handleSalaryChoiceWarning();
      }
    }

    this.validateContractOptionsDebounced();
  };

  validateContractOptions = () => {
    timeReportService
      .validateConsultantContractOptions({
        consultantContractPriceRecordDto: this.form.values.SalaryRecords.filter(
          (x) => !x.deleted
        ).map((x) => ({
          id: x._id,
          payPeriod: x.period,
          name: "",
          endDay: x.endDay || "",
          startDay: x.startDay || "",
          from: x.from,
          to: x.to,
          workType: x.workType,
          paidPer: x.paidPer,
          currency: x.currency,
          price: 0,
          startDate: new Date("2021-01-01").toISOString(),
          endDate: new Date("2029-01-01").toISOString(),
          specialPeriodId: "",
          specialPeriod: {
            id: "",
            title: "",
            price: 0,
            startDate: new Date("2021-01-01").toISOString(),
            endDate: new Date("2029-01-01").toISOString()
          }
        }))
      })
      .then((res) => {
        console.log("validation result assigning");
        this.validationResult = res.data;
      })
      .catch((e) => {
        console.error(e);
        this.validationResult = [];
      });
  };

  validateContractOptionsDebounced = debounce(this.validateContractOptions, 1000);

  handleSalaryChoiceWarning() {
    const contractLengthMonths = moment(this.form.values.EndDate).diff(
      moment(this.form.values.StartDate),
      "months",
      true
    );
    const monthsSinceLastContract = this.lastContract?.IsSigned
      ? moment(this.form.values.StartDate).diff(
          moment(this.lastContract.EndDate),
          "months",
          true
        )
      : null;
    const warning = getContractWarning({
      selection: this.form.values.SalaryChoice,
      contractLengthMonths,
      monthsSinceLastContract
    });

    if (warning) {
      this.isSalaryChoiceDialogOpen = true;
      const options = new OkDialogOptions(null);
      options.message = warning;
      store()
        .ui.confirmDialog.confirm(options)
        .then((res) => {
          if (res === "ok") {
            this.isSalaryChoiceDialogRequired = false;
            this.isSalaryChoiceDialogOpen = false;
          } else {
            this.addEditsProofFields();
          }
        });
    }
  }

  @computed get isBCContractTypes() {
    const hasBCType =
      this.currentConsultant?.AvailableForWorkInCountries.includes("Bonliva Care");

    const hasBCProfession = this.currentConsultant?.Professions.some((x) =>
      bc_professions.includes(x)
    );

    return hasBCType && hasBCProfession;
  }

  @computed get showSendToOneFlow() {
    if (!this.contractStrategy) {
      return false;
    }

    return (
      this.contractStrategy?.canPublishToOneflowDirectly({
        consultantBirthDate: moment(this.currentConsultant.BirthDate).toDate(),
        consultantLegalPersonType: this.currentConsultant.LegalPersonType,
        tenant: this.tenant
      }) || false
    );
  }

  @computed get hasBiomedicinskAnalytikerProfession(): boolean {
    return this.currentConsultant?.Professions?.includes("Biomedicinsk analytiker");
  }

  @computed get hasNationalProcurementSelection(): boolean {
    return (
      this.tenant !== "wcc" &&
      this.company === "AB" &&
      this.action !== "duplicate" &&
      !this.hasBiomedicinskAnalytikerProfession
    );
  }

  @computed get showCompanySelect() {
    if (this.tenant !== "bonliva-ab") {
      return false;
    }

    // Hide company selection for Biomedicinsk analytiker (always AB)
    if (this.hasBiomedicinskAnalytikerProfession) {
      return false;
    }

    return this.currentConsultant?.AvailableForWorkInCountries.length > 1;
  }

  @action addEditsProofFields = () => {
    const payoutType =
      this.currentConsultant?.LegalPersonType === LegalPersonType.Employee
        ? payoutTypeEmp
        : payoutTypeSub;
    this._fields = [...this._fields, payoutType, editsProof];
    this.form.setValue("editsProof", defaultProof);
    this.form.validate();
  };

  @action resetForm = () => {
    const { highHolidays } = this.form.values;
    if (this.currentConsultant) {
      this.form.reset({
        ...defaultValues,
        highHolidays,
        ConsultantId: this.currentConsultant._id,
        SalaryChoice: this.currentConsultant?.contactType?.employeeContractType
      });

      if (this.currentConsultant.Professions?.length === 1) {
        const profession = this.currentConsultant.Professions[0];

        if (
          this.company === "AB" ||
          (this.company === "BC" && bc_professions.includes(profession))
        ) {
          this.form.values.Profession = profession;
        }
      }

      if (this.currentConsultant.AreasOfExpertise?.length === 1) {
        this.form.values.AreaOfExpertise = this.currentConsultant.AreasOfExpertise[0];
      }
    } else {
      this.form.reset({
        ...defaultValues,
        highHolidays
      });
    }
  };

  @action setCompany = (v: CompanyName) => {
    if (v === "AB" && this.isNationalProcurementDisabled) {
      this.isNationalProcurement = "false";
    }
    this.company = v;
    this.resetForm(); // reset form to clear validation errors
  };

  @action setNational = (v: RadioTrueFalse) => {
    this.isNationalProcurement = v;
    if (this.action !== "duplicate") {
      this.resetForm(); // reset form to clear validation errors
    } else {
      // when duplicate, we need set correct contractType value for NationalProcurement
      const type = this.form.values.ContractType;
      let resultType = null;
      if (v === "true") {
        switch (type) {
          case ConsultantContractType.Framework:
            resultType = ConsultantContractType.Framework_National;
            break;
          case ConsultantContractType.Intermittent:
            resultType = ConsultantContractType.Intermittent_National;
            break;
          case ConsultantContractType.Outsource:
            resultType = ConsultantContractType.Outsource_National;
            break;
          case ConsultantContractType.Employment:
            resultType = ConsultantContractType.Employment_National;
            break;
        }
      }
      if (v === "false") {
        switch (type) {
          case ConsultantContractType.Framework_National:
            resultType = ConsultantContractType.Framework;
            break;
          case ConsultantContractType.Intermittent_National:
            resultType = ConsultantContractType.Intermittent;
            break;
          case ConsultantContractType.Outsource_National:
            resultType = ConsultantContractType.Outsource;
            break;
          case ConsultantContractType.Employment_National:
            resultType = ConsultantContractType.Employment;
            break;
        }
      }
      this.form.setValue("ContractType", resultType);
    }
  };

  setRelevantValues = (values: FormData) => {
    const { ContractType, Profession, highHolidays } = values;
    const s: any = getSchema(contracts[ContractType][Profession]) ?? [];
    const allowedFields = Object.keys(s?.fields) ?? [];
    const newValues = defaultValues;

    for (const k of Object.keys(values)) {
      if (allowedFields.includes(k)) {
        newValues[k] = values[k];
      } else {
        newValues[k] = defaultValues[k];
      }
    }
    this.form.reset({
      ...newValues,
      highHolidays,
      ConsultantId: this.currentConsultant._id
    });
  };

  @action onChangeContractType = (v: RadioTrueFalse) => {
    const contractType = this.form.values.ContractType;

    const newContractType = formatContractTypeByNational(contractType, v === "true");

    if (newContractType !== contractType) {
      this.isNationalProcurement = National_contractsTypes.includes(newContractType)
        ? "true"
        : "false";

      this.form.setValue("ContractType", newContractType);
      this.setRelevantValues(this.form.values);
    }

    this.showContractTypeChangeSelect = false;
  };

  @computed get canEditSignedWithoutProof() {
    return store().user.inTeam("Ekonomi och Avtal") || store().user.inTeam("IT");
  }

  getPriceTemplates = async (text?: string) => {
    const { data } = await ContractsPriceTemplates.func(
      {
        filter: { text },
        skip: 0,
        limit: 0,
        onlyPrivate: false
      },
      { fetchPolicy: "network-only" }
    );
    if (data.list) {
      this.templates = data.list.items.filter((x) => !x.private);
      this.fav = data.list.items.filter((x) => x.private);
    }
  };

  getHolidayOptions = () => {
    ConsultantContract_getHighHolidays.func({}, { fetchPolicy: "cache-first" }).then(
      (res) => {
        if (res.data.getHighHolidays) {
          this.highHolidaysOptions = res.data.getHighHolidays;
          if (this.form.values.highHolidays?.length === 0) {
            this.form.setValue(
              "highHolidays",
              mergeHighHolidays(this.highHolidaysOptions, [])
            );
          }
        }
      }
    );
  };

  getConsultantPayoutByYear = (consultantId: string) => {
    if (!consultantId) {
      return;
    }
    ConsultantContract_getConsultantPayoutByYear.func({
      consultantId: consultantId
    })
      .then((res) => {
        if (res.data.getConsultantPayoutByYear) {
          this.payouts = res.data.getConsultantPayoutByYear;
        }
      })
      .catch((e) => {
        store().snackbars.error(`${e}`);
      });
  };

  getCurrentYearPayoutForNationalProcurement = (): number => {
    return (
      this.payouts?.find((x) => x.year === moment().year())?.totalNationalProcurement ?? 0
    );
  };

  @action setNoTemplateWarning = (text: string) => {
    this.defaultTemplateWarning = text;
  };

  @action setDisabledSalaryRecord = (v: boolean) => {
    this.disabledSalaryRecord = v;
  };

  @action setFormattedDataToForm = (data) => {
    const {
      Type,
      Bonus,
      CustomerName,
      FileName,
      Dates,
      HospitalRef,
      DepartmentsRefs,
      SpecificRequirements,
      OtherRequirements,
      StartDate,
      EndDate,
      highHolidays,
      DateFrameworkContractTypeIsSigned,
      SignedOn,
      ...rest
    } = data;
    this.fileName = FileName;
    this.preFilledData = {
      ...rest,
      Bonus: Bonus ?? [],
      StartDate: StartDate ? moment(StartDate, "x") : null,
      EndDate: EndDate ? moment(EndDate, "x") : null,
      DateFrameworkContractTypeIsSigned: DateFrameworkContractTypeIsSigned
        ? moment(DateFrameworkContractTypeIsSigned, "x")
        : null,
      ContractType: Type,
      SpecificRequirements:
        SpecificRequirements?.length === 0 ? [""] : SpecificRequirements,
      OtherRequirements: OtherRequirements?.length === 0 ? [""] : OtherRequirements,
      highHolidays: mergeHighHolidays(this.highHolidaysOptions, highHolidays),
      HospitalRef: HospitalRef
        ? { label: HospitalRef.Name, value: HospitalRef.Id }
        : null,
      DepartmentsRefs: DepartmentsRefs ? DepartmentsRefs.map((x) => x.Id) : null
    };
    this.ticketData = { CustomerName, Dates, Type };
  };

  dropMilePeriodIfNeed = (data: ContractDataForDuplicationFragment) => {
    if (data.Travels?.MaxCost?.Period === ConsultantContractTravelsPeriod.Mile) {
      const newData = cloneDeep(data);
      newData.Travels.MaxCost.Period = null;

      return newData;
    }

    return data;
  };

  @action getContractData = async (contractId, action) => {
    this.loading = true;
    try {
      await this.getPriceTemplates();
      if (action === "edit") {
        await ConsultantContract_getByIdForEdit.func(
          { contractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          const data = res.data.getConsultantContractDetailInfo;
          this.contractData = data;
          this.setFormattedDataToForm(this.dropMilePeriodIfNeed(data));
        });
      } else if (action === "duplicate") {
        await ConsultantContract_getByIdForDuplication.func(
          { contractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          let data = res.data.getConsultantContractDetailInfo;
          if (data.ContractPriceTemplateId) {
            const template = [
              ...this.templates,
              ...this.fav,
              ...this.customerPriceTemplates
            ].find((x) => x._id === data.ContractPriceTemplateId);

            const t = cloneDeep(template);
            if (t) {
              data = {
                ...data,
                SalaryRecords: t.records,
                highHolidays: t.highHolidays
              };
            } else {
              this.setNoTemplateWarning("Oops, det verkar inte finnas någon avtalsmall");
            }
          }

          if (
            national_professions.includes(data.Profession) &&
            !BC_contractsTypes.includes(data.Type)
          ) {
            this.setCompany("AB");
            this.showContractTypeChangeSelect = true;
          }

          this.setFormattedDataToForm(this.dropMilePeriodIfNeed(data));
        });
      } else if (action === "forConfirmation") {
        await ConsultantContract_getByIdForConfirmation.func(
          { contractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          const { getConsultantContractDetailInfo: parent } = res?.data ?? {};
          const confirmationType = getConfirmationContractType(parent.Type);

          this.isNationalProcurement = National_contractsTypes.includes(confirmationType)
            ? "true"
            : "false";

          this.setFormattedDataToForm({
            ...defaultValues,
            ConsultantId: parent.ConsultantId,
            ParentContractId: parent._id,
            SalaryChoice: parent.SalaryChoice,
            Country: parent.Country,
            BasicPrice: parent.BasicPrice,
            SalaryRecords: parent.SalaryRecords ?? [
              BC_contractsTypes.includes(confirmationType)
                ? ContractPriceTemplateRecordInput.CreateBlankBC()
                : ContractPriceTemplateRecordInput.CreateBlank()
            ],
            SpecificPeriodPrices: parent.SpecificPeriodPrices,
            highHolidays: parent.highHolidays,
            Type: confirmationType,
            Profession: parent.Profession,
            DateFrameworkContractTypeIsSigned: moment(parent.SignedOn).format("x"),
            isNationalProcurementPriceStrategy: parent.isNationalProcurementPriceStrategy
          });
        });
      }
    } catch (e) {
      store().snackbars.error(`${e}`);
    } finally {
      this.loading = false;
      this.validateContractOptionsDebounced();
    }
  };

  @action getCurrentConsultant = (id) => {
    if (!id) {
      store().snackbars.error("Consultant id is missing, cannot open the contract");
      return;
    }
    ConsultantContract_GetConsultant.func({ id }, { fetchPolicy: "network-only" })
      .then((res) => {
        const { consultantById } = res?.data ?? {};
        this.form.values.ConsultantId = consultantById._id;
        this.currentConsultant = res.data.consultantById;
        this.lastContract = res.data.lastContract || null;

        // Auto-set company and national procurement for Biomedicinsk analytiker
        if (this.hasBiomedicinskAnalytikerProfession) {
          this.setCompany("AB");
          this.isNationalProcurement = "false";
        } else {
          const hasBCType =
            this.currentConsultant.AvailableForWorkInCountries.includes("Bonliva Care");
          const hasBothCompanies =
            this.currentConsultant.AvailableForWorkInCountries.length > 1;
          if (!hasBothCompanies) {
            this.setCompany(hasBCType ? "BC" : "AB");
          }
        }
      })
      .catch((e) => {
        store().snackbars.error(`${e}`);
      });

    // set default country in which the consultant will work
    this.form.values.Country = this.inNorwayTeam ? "Norway" : "Sweden";
  };

  @action selectCustomer = (id) => {
    if (!id) {
      return;
    }
    ConsultantContract_GetCustomer.func({ id }).then((res) => {
      if (res) {
        const { customerById } = res.data;
        this.selectedCustomer = customerById;
        this.form.values.Address = customerById.Address;
        this.fetchIsParentKommun(customerById.RegionRef?.Id, false);
      }
    });
  };

  @action private fetchIsParentKommun = (parentId: string, updateShowOrgName: boolean) => {
    if (!parentId) {
      this.isParentKommun = false;
      return;
    }
    ConsultantContract_GetParentOrganizationType.func({ id: parentId }).then((res) => {
      this.isParentKommun =
        res?.data?.customerById?.OrganizationType === OrganizationType.Commune;
      if (updateShowOrgName) {
        this.form.values.showOrgName = this.isParentKommun;
      }
    });
  };

  @computed get isNationalProcurementDisabled(): boolean {
    // Always disable national procurement for Biomedicinsk analytiker
    if (this.hasBiomedicinskAnalytikerProfession) {
      return true;
    }

    return !this.currentConsultant?.Professions.some((x) =>
      national_professions.includes(x)
    );
  }

  @computed get allProfessions() {
    if (this.isBCContractTypeSelected) {
      return (
        this.currentConsultant?.Professions.filter((x) => bc_professions.includes(x)) ||
        []
      );
    }
    if (this.isNationalContractTypeSelected) {
      return (
        this.currentConsultant?.Professions.filter((x) =>
          national_professions.includes(x)
        ) || []
      );
    }
    return this.currentConsultant?.Professions || [];
  }

  @computed get typeOptions(): ValueLabel[] {
    if (this.currentConsultant?.LegalPersonType === LegalPersonType.Employee) {
      if (this.company === "BC") {
        return BC_contractEmployeesTypeOptions;
      }
      if (this.company === "AB") {
        return this.isNationalProcurement === "true"
          ? notional_contractEmployeesTypeOptions
          : contractEmployeesTypeOptions;
      }
      if (!this.company) {
        return this.isNationalProcurement === "true"
          ? notional_contractEmployeesTypeOptions
          : contractEmployeesTypeOptions;
      }
    } else {
      // subcontractor
      if (this.company === "BC") {
        return BC_contractSubTypeOptions;
      }
      if (this.company === "AB") {
        return this.isNationalProcurement === "true"
          ? notional_contractSubTypeOptions
          : contractSubTypeOptions;
      }
      if (!this.company) {
        return this.isNationalProcurement === "true"
          ? notional_contractSubTypeOptions
          : contractSubTypeOptions;
      }
    }
    return [];
  }

  @computed get allCountries() {
    return store()
      .cache.allCountriesWorldwide.filter((c) => c.canWorkIn)
      .map((c) => c.name);
  }

  @computed get allSpecializations() {
    return this.currentConsultant?.Specializations || [];
  }

  @computed get allAreasOfExpertise() {
    return this.currentConsultant?.AreasOfExpertise || [];
  }

  @computed get hasErrorsInMainTab(): boolean {
    return (
      Object.keys(this.form.errors).filter(
        (x) => x !== "BasicPrice" && x !== "SpecificPeriodPrices" && x !== "SalaryRecords"
      )?.length > 0
    );
  }

  @computed get hasErrorsInSalaryOption(): boolean {
    return this.validationResult?.some((x) => !x.isValid);
  }

  @computed get showMainForm(): boolean {
    return (
      this.company === "BC" ||
      this.tenant === "wcc" ||
      (this.company === "AB" && this.isNationalProcurement !== null)
    );
  }

  @computed get canSubmit(): boolean {
    return (
      !this.noFields &&
      !this.hasErrorsInMainTab &&
      this.form.canSubmit &&
      this.showMainForm &&
      !this.showContractTypeChangeSelect
    );
  }

  @computed get isShowAdditionFields(): boolean {
    const { errors } = this.form;
    return Object.keys(errors).filter((x) => this.minReq.includes(x)).length === 0;
  }

  @computed get isShowDateFields(): boolean {
    if (
      this.form.values.ContractType === ConsultantContractType.Intermittent_National ||
      this.form.values.ContractType === ConsultantContractType.Employment_National
    ) {
      return true;
    } else {
      return this.isShowAdditionFields;
    }
  }

  @computed get consultantPension() {
    const { values } = this.form;

    // if consultant is subcontractor, we don't need to show pension options
    if (this.currentConsultant?.LegalPersonType === LegalPersonType.Subcontractor) {
      return null;
    }

    if (!this.currentConsultant?.BirthDate || !values.StartDate || !values.EndDate) {
      return null;
    }

    return getConsultantAgeGroup(
      moment(this.currentConsultant.BirthDate).format(dateFormat),
      moment(values.StartDate).format(dateFormat),
      moment(values.EndDate).format(dateFormat)
    );
  }

  @computed get become25and65dates() {
    return {
      turns25Date: moment(this.currentConsultant.BirthDate).add(25, "years").toDate(),
      turns65Date: moment(this.currentConsultant.BirthDate).add(65, "years").toDate()
    };
  }

  @computed get isBCContractTypeSelected(): boolean {
    return BC_contractsTypes.includes(this.form?.values?.ContractType);
  }

  @computed get isNationalContractTypeSelected(): boolean {
    return National_contractsTypes.includes(this.form?.values?.ContractType);
  }

  @computed get diffBetweenCustomerTemplate(): boolean {
    const mergedHH = mergeHighHolidays(
      this.highHolidaysOptions,
      this.selectedCustomerTemplate?.highHolidays
    );
    const customerTemplate = {
      highHolidays: sortBy(
        mergedHH?.map((x: any) => ({
          isGrateHoliday: x.isGreatHoliday,
          name: x.option.name
        })),
        "name"
      ),
      records: this.selectedCustomerTemplate?.records?.map((x) => ({
        articleNumber: x.articleNumber,
        currency: x.currency,
        deleted: x.deleted,
        endDay: x.endDay,
        startDay: x.startDay,
        from: x.from,
        paidPer: x.paidPer,
        period: x.period,
        to: x.to,
        workType: x.workType
      }))
    };
    const currentTemplate = {
      highHolidays: sortBy(
        this.form.values.highHolidays?.map((x) => ({
          isGrateHoliday: x.isGreatHoliday,
          name: x.option.name
        })),
        "name"
      ),
      records: this.form.values.SalaryRecords?.map((x) => ({
        articleNumber: x.articleNumber,
        currency: x.currency,
        deleted: x.deleted,
        endDay: x.endDay,
        startDay: x.startDay,
        from: x.from,
        paidPer: x.paidPer,
        period: x.period,
        to: x.to,
        workType: x.workType
      }))
    };
    // console.log("check", customerTemplate, currentTemplate);
    return !isEqual(customerTemplate, currentTemplate);
  }

  @action fetchHospitalData = (id) => {
    if (!id) {
      return;
    }

    this.isLoadingDepartments = true;

    BookingEdit_FetchDepts.func({ hospitalId: id }, { fetchPolicy: "network-only" })
      .then(({ data }) => {
        this.allDepartments = data.departments.items.toLabelValue(
          (x) => x.Name,
          (x) => x._id
        );
      })
      .then((res) => {
        if (this.allDepartments?.length > 0 && !this.form.values.DepartmentsRefs) {
          this.form.setError(
            "DepartmentsRefs",
            locale.fieldIsMandatory(locale.Department)
          );
        }
      });
  };

  formatCustomerPriceTemplatesOptions = (
    contracts: CustomerContracts,
    consultantBookingContracts: ConsultantContract_getCustomerPriceList.Return["consultantBookingContracts"]
  ) => {
    // console.log("c", toJS(contracts));
    const priceLists = contracts
      ?.reduce<CustomerPriceListItem[]>((a, c) => {
        return a.concat(c.priceLists);
      }, [])
      .map((x) => ({
        ...x,
        priceGroups: x.priceGroups.filter((prof) => {
          const aoe = this.form.getValue("AreaOfExpertise");
          const profNames = store().cache.getProfessionsOnAreasofExp([aoe] as string[]);
          // console.log(prof.professionsPrice, profNames);
          return this.form.getValue("AreaOfExpertise")
            ? prof.professionsPrice?.any(
                (p) =>
                  p.profession.areaOfExpertise === aoe ||
                  (!p.profession.areaOfExpertise && profNames.includes(p.profession.name))
              )
            : true;
        })
      }));
    // console.log("priceLists", priceLists);
    if (priceLists || consultantBookingContracts) {
      this.customerPriceTemplates = priceLists.map((x) => x.priceGroups).flat();
      const options = priceLists
        .map((x) => {
          const archived = checkArchivedPriceList(x, this.form.values);

          return x.priceGroups.map((p, i) => ({
            label: `${x.name} ${moment(x.startDate).format("DD/MM/YYYY")} - ${moment(
              x.endDate
            ).format("DD/MM/YYYY")}`,
            value: p._id,
            archived
          }));
        })
        .flat();

      const consultantBookingContractsOpt = consultantBookingContracts.map((x) => {
        const archived = checkArchivedPriceList(
          {
            startDate: moment(x.StartDate, "x"),
            endDate: moment(x.EndDate, "x")
          },
          this.form.values
        );

        return {
          label: `${locale[x.Type]} ${moment(x.StartDate, "x").format(
            "DD/MM/YYYY"
          )} - ${moment(x.EndDate, "x").format("DD/MM/YYYY")}`,
          value: x._id,
          archived
        };
      });

      this.customerPriceOptions = sortPriceOptions([
        ...options,
        ...consultantBookingContractsOpt
      ]);
    }
  };

  @action getCustomerContractPriceLists = (customerIds: string[]) => {
    if (customerIds.filter((x) => x)?.length > 0) {
      ConsultantContract_getCustomerPriceList.func(
        {
          consultantId: this.currentConsultant._id,
          customerIds
        },
        { fetchPolicy: "network-only" }
      ).then(({ data }) => {
        if (data.getCustomerContractPriceLists || data.consultantBookingContracts) {
          this.customerContracts = data.getCustomerContractPriceLists;
          this.consultantBookingContracts = data.consultantBookingContracts;
          this.formatCustomerPriceTemplatesOptions(
            data.getCustomerContractPriceLists,
            data.consultantBookingContracts
          );
        }
      });
    }
  };

  @action setPensionSolution(value: ConsultantContractPensionSolution | null) {
    this.form.setValue("pensionSolution", value);

    if (
      (this.consultantPension?.becomes25 || this.consultantPension?.becomes65) &&
      value === ConsultantContractPensionSolution.Split
    ) {
      this.maxEndDate = moment(
        this.consultantPension.becomes25
          ? this.become25and65dates.turns25Date
          : this.become25and65dates.turns65Date
      )
        .subtract(1, "days")
        .toDate();

      this.form.setValue("EndDate", null);
    } else {
      this.maxEndDate = null;
    }
  }

  @action setHospital = (hospital: Customer) => {
    this.selectedCustomer = hospital;
    this.originalCustomer = hospital;

    if (!hospital) {
      this.form.values.HospitalRef = null;
      this.form.values.DepartmentsRefs = null;
      this.form.values.Address = initAddress;
      this.allDepartments = null;
      this.isParentKommun = false;
      this.form.validate();
      return;
    }

    this.form.values.HospitalRef = {
      label: hospital.Name,
      value: hospital._id
    };
    this.form.values.DepartmentsRefs = null;
    this.form.values.Address = hospital.Address;
    this.fetchHospitalData(hospital._id);

    const regionParentId = hospital.Parents?.find(
      (p) => p.Type === CustomerType.Region
    )?.Id;
    this.fetchIsParentKommun(regionParentId, true);

    this.getCustomerContractPriceLists([hospital._id]);

    this.form.validate();
  };

  @action setDepartments = (depts) => {
    this.form.values.DepartmentsRefs = depts;
    this.form.validate();
    // if (depts.length === 1) {
    //   this.selectCustomer(depts[0]);
    // } else {
    //   this.form.setValue("Address", this.originalCustomer.Address);
    // }
    this.getCustomerContractPriceLists(depts);
  };

  @action addSalary = () => {
    this.form.push(
      "SalaryRecords",
      this.isBCContractTypeSelected
        ? ContractPriceTemplateRecordInput.CreateBlankBC()
        : ContractPriceTemplateRecordInput.CreateBlank()
    );
  };

  @action duplicateSalary = (item: ContractPriceTemplateRecordInput) => {
    const temp = { ...item };
    temp._id = createObjectID();
    this.form.push("SalaryRecords", temp);
  };

  @action handleChangeBonusType = (value) => {
    this.form.setValue("bonusType", value);
  };

  @action addBonusRecord = () => {
    let bonusBlank = null;
    if (this.form.values.bonusType === "WeeklyHoursWorkedBonus") {
      bonusBlank = WeeklyHoursWorkedBonus.BlankState();
    } else if (this.form.values.bonusType === "WorkedHourBonus") {
      bonusBlank = WorkedHourBonus.BlankState();
    } else if (this.form.values.bonusType === "WorkedHourPeriodBonus") {
      bonusBlank = WorkedHourPeriodBonus.BlankState();
    } else if (this.form.values.bonusType === "OvertimeBonus") {
      bonusBlank = OvertimeBonus.BlankState();
    } else if (this.form.values.bonusType === "CustomBonus") {
      bonusBlank = CustomBonus.BlankState();
    }

    if (bonusBlank) {
      this.form.push("Bonus", bonusBlank);
      this.form.setValue("bonusType", null);
    }
  };

  @action removeBonusRecord = (index: number) => {
    this.form.removeByIndex("Bonus", index);
  };

  @action addSpecificPeriodPrice = () => {
    this.form.push("SpecificPeriodPrices", oneSpecificPeriodPrices);
  };

  @action remSpecificPeriodPrice = (index: number) => {
    this.form.removeByIndex("SpecificPeriodPrices", index);
  };

  setDefaultHolidaysAndSalaryRecords = () => {
    const mergedHolidays = mergeHighHolidays(this.highHolidaysOptions, []);
    this.form.setValue(`SalaryRecords`, [ContractPriceTemplateRecordInput.CreateBlank()]);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action handleChangeTemplate = (templateId: string) => {
    if (!templateId) {
      this.form.setValue(`template`, templateId);
      this.setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = this.templates.find((x) => x._id === templateId);
    const mergedHolidays = mergeHighHolidays(
      this.highHolidaysOptions,
      template.highHolidays
    );
    this.form.setValue(`template`, templateId);
    this.form.setValue(`favTemplate`, null);
    this.form.setValue(`customerTemplate`, null);
    this.form.setValue(`SalaryRecords`, template?.records);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action handleChangeFavTemplate = (v) => {
    if (!v) {
      this.form.setValue(`favTemplate`, v);
      this.setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = this.fav.find((x) => x._id === v);

    const mergedHolidays = mergeHighHolidays(
      this.highHolidaysOptions,
      template.highHolidays
    );

    this.form.setValue(`favTemplate`, v);
    this.form.setValue(`template`, null);
    this.form.setValue(`customerTemplate`, null);
    this.form.setValue(`SalaryRecords`, template?.records);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action handleChangeCustomerTemplate = (v) => {
    if (!v) {
      this.form.setValue(`customerTemplate`, v);
      this.setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = this.customerPriceTemplates.find((x) => x._id === v);
    const consultantBookingContract = this.consultantBookingContracts.find(
      (x) => x._id === v
    );

    this.selectedCustomerTemplate = template
      ? {
          highHolidays: template?.highHolidays,
          records: template?.records
        }
      : {
          highHolidays: consultantBookingContract?.highHolidays,
          records: consultantBookingContract?.SalaryRecords
        };

    const records = (template?.records || consultantBookingContract?.SalaryRecords) ?? [];

    const mergedHolidays = mergeHighHolidays(
      this.highHolidaysOptions,
      template?.highHolidays
    );
    this.form.setValue(`customerTemplate`, v);
    this.form.setValue(`template`, null);
    this.form.setValue(`favTemplate`, null);
    this.form.setValue(`SalaryRecords`, records);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  // @action removeSalary = (index: number) => {
  //   this.form.removeByIndex("Salary", index);
  // };

  @action addRequirements = (name) => {
    this.form.push(name, "");
  };

  @action removeRequirements = (name: string, index: number) => {
    this.form.removeByIndex(name, index);
  };

  @action pushToArr = (name, value) => {
    if (this.form.getValue(name)) {
      this.form.push(name, value);
    } else {
      this.form.setValue(name, [value]);
    }
  };

  @action removeByIndex = (name: string, index: number) => {
    this.form.removeByIndex(name, index);
  };

  download = (link: string) => {
    const a = document.createElement("a");
    a.href = link;
    a.click();
  };

  @action handleTicket = (data: ConsultantContractTableRowDataFragment) => {
    const title = `Skicka ${locale.consultantContractTypes[data.Type]}${
      data.CustomerName && ` gällande ${data.CustomerName}`
    }, ${data.Dates}`;

    const consultantLink = `${window.location.origin}/consultants/${this.currentConsultant._id}`;
    SendEmail.func({
      input: {
        recipients: [
          {
            email: replaceEmailDomainByTenant("service@bonliva.se", this.tenant)
          }
        ],
        from: store()?.profile?.Email,
        subject: title,
        body: `<p>${title}</p><br><a href="${consultantLink}">${consultantLink}</a><br><strong>Avtal</strong>`
      }
    }).then(() => {
      PublishConsultantContract.func({
        contractId: data._id
      });
    });
  };

  @action handleSaveAndSendToOneflow = () => {
    this.isPublishingToOneFlow = true;
    this.form
      .submitAsync()
      .then((result) => {
        return PublishConsultantContract.func({
          contractId: result._id
        });
      })
      .then(
        ({ data }) => {
          const url = data.publishContract.contractUrl;
          openBlank(url);
          store().snackbars.success("Published");
          this.handleClose();
          this.isPublishingToOneFlow = false;

          getObservableQuery(
            ConsultantContract_getContractsByConsultantId.OperationName
          )?.refetch();
        },
        (error) => {
          this.isPublishingToOneFlow = false;
          store().snackbars.error(error.message);
        }
      );
  };

  @action handleSaveAndSendViaWorkflow = (forcePublish: boolean) => {
    this.isPublishingToOneFlow = true;
    this.form
      .submitAsync()
      .then((result) => {
        return PublishConsultantContractViaWorkflow.func({
          contractId: result._id,
          forcePublish
        });
      })
      .then(
        ({ data }) => {
          const url = data.publishContractViaWorkflow.contractUrl;
          if (!forcePublish) {
            openBlank(url);
            store().snackbars.success("Spara och ta mig till OneFloW");
          } else {
            store().snackbars.success("Spara och skicka via OneFlow");
          }
          this.handleClose();
          this.isPublishingToOneFlow = false;

          getObservableQuery(
            ConsultantContract_getContractsByConsultantId.OperationName
          )?.refetch();
        },
        (error) => {
          this.isPublishingToOneFlow = false;
          store().snackbars.error(error.message);
        }
      );
  };

  requestUpload = async () =>
    store().upload.requestFileUpload({
      multiple: true
    });

  @action private onSubmit = async () => {
    return this.submit()
      .then((result) => {
        this.handleClose();
        if (this.isTicket) {
          this.handleTicket(result);
        }
        return result; // results is used in chain when you publish contract to oneflow
      })
      .catch((e) => {
        store().snackbars.error(`${e}`);
      })
      .finally(() => {
        this.loading = false;
        this.isTicket = false;
      });
  };

  @action private submit = async () => {
    const { valid, values } = this.form.state;

    if (valid) {
      this.loading = true;
      const BasicPrice = Number(values.BasicPrice);
      const Bonus = values.Bonus?.map((x) => ({ ...x, Amount: Number(x.Amount) }));
      const IntroductionPrice = values.IntroductionPrice?.filter(
        (el) => el.Description.length > 0 && Number(el.Sum) >= 0
      ).map((x) => ({ ...x, Sum: Number(x.Sum) }));
      const SalaryRecords = values.SalaryRecords.filter((x) => !x.deleted).map((x) => ({
        ...omit(x, "id", "isFreeInput"),
        articleNumber: x.articleNumber || null,
        paidPer: x.paidPer || null,
        currency: x.currency || null,
        workType: x.workType || null,
        period: x.period || null,
        from: x.from || null,
        to: x.to || null,
        endDay: x.endDay || null,
        startDay: x.startDay || null,
        fixedPrice: x.fixedPrice ? Number(x.fixedPrice) : null,
        priceMultiplier: x.priceMultiplier ? Number(x.priceMultiplier) : null,
        customPrice: x.customPrice ? Number(x.customPrice) : null
      }));

      const SpecificPeriodPrices = values.SpecificPeriodPrices?.filter(
        (el) => Number(el.price) > 0
      ).map((x) => ({ ...x, price: Number(x.price) }));

      const Accommodation = {
        ...values.Accommodation,
        MaxCost: {
          ...values.Accommodation.MaxCost,
          Price: Number(values.Accommodation.MaxCost?.Price)
        }
      };
      const Travels = {
        ...values.Travels,
        MaxCost: {
          ...values.Travels.MaxCost,
          Price: Number(values.Travels.MaxCost?.Price)
        },
        TravelTemplateValue: Number(values.Travels?.TravelTemplateValue) || null
      };

      const SpecificRequirements = values.SpecificRequirements.filter((el) => el?.length);
      const OtherRequirements = values.OtherRequirements.filter((el) => el?.length);
      const {
        ConsultantId,
        _id,
        // exclude some fields
        template,
        favTemplate,
        customerTemplate,
        editsProof,
        bonusType,
        ...rest
      } = values;
      const HospitalRef = values.HospitalRef ? { Id: values.HospitalRef?.value } : null;
      const YoEx = values.YoEx || null;
      const DepartmentsRefs = values.DepartmentsRefs
        ? values.DepartmentsRefs.map((x) => ({ Id: x }))
        : null;
      const ContractPriceTemplateId = template || favTemplate || customerTemplate;

      const editsProofFileIds = [];
      if (editsProof?.files.length) {
        for (const file of editsProof.files) {
          await UploadConsultantContractDocument.func({ file }).then((x) =>
            editsProofFileIds.push(x.data.uploadDocument.FileRef.NodeID)
          );
        }
      }

      const data = {
        ...rest,
        ContractPriceTemplateId,
        BasicPrice,
        Bonus,
        IntroductionPrice,
        SpecificPeriodPrices,
        SalaryRecords,
        SpecificRequirements,
        OtherRequirements,
        HospitalRef,
        DepartmentsRefs,
        Accommodation,
        Travels,
        YoEx,
        editsProofFileIds,
        isNationalProcurementPriceStrategy: this.isNationalProcurementPriceStrategy
      };

      const promise = this.isEdit
        ? ConsultantContract_Edit.func(
            { contractId: _id, data },
            {
              refetchQueries: [
                ConsultantContract_getContractsByConsultantId.OperationName,
                GetConsultantContracts.OperationName
              ]
            }
          ).then((res) => {
            store().snackbars.success(locale.EditContractSuccess);
            return res.data.editUnSignConsultantContract;
          })
        : ConsultantContract_AddUnSign.func(
            {
              consultantId: ConsultantId,
              data
            },
            {
              refetchQueries: [
                ConsultantContract_getContractsByConsultantId.OperationName,
                ConsultantEventLog_GetEvents.OperationName,
                GetConsultantContracts.OperationName
              ]
            }
          ).then((res) => {
            store().snackbars.success(locale.AddContractSuccess);
            return res.data.addUnSignConsultantContract;
          });

      return promise;
    }
  };
}
